import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Alert, Button, Chip, CircularProgress, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";
import AllocateCallModal from "@/components/admin/dialpad/AllocateCallModal";
import CallDetailModal from "@/components/admin/dialpad/CallDetailModal";
import AdminBackLink from "@/components/admin/dialpad/AdminBackLink";

const DIALPAD_PINK = "#E63888";

function fmtDuration(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const AdminDialpadUnallocatedPage = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [allocating, setAllocating] = useState(null);
  const [viewingCall, setViewingCall] = useState(null);
  const [archiving, setArchiving] = useState(null);   // call row pending confirm
  const [archivingId, setArchivingId] = useState(null); // id being saved

  // Bulk archive
  const [selectionModel, setSelectionModel] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Re-scan state
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // { matched, total_scanned }

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await phappsRequest(
        `${API_PATHS.DIALPAD_CALLS_UNALLOCATED}?page=${page + 1}&limit=50`, "GET"
      );
      setRows((data?.calls ?? []).map((r, i) => ({ ...r, id: r.id ?? i })));
      setTotal(data?.total ?? 0);
    } catch {
      setError("Failed to load unallocated calls");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const handleRescan = async () => {
    setScanning(true);
    setScanResult(null);
    setError(null);
    try {
      const result = await phappsRequest(API_PATHS.DIALPAD_CALLS_RESCAN, "POST", {});
      setScanResult(result);
      // Refresh list if any were matched
      if (result.matched > 0) fetchCalls();
    } catch {
      setError("Re-scan failed. Please try again.");
    } finally {
      setScanning(false);
    }
  };

  const handleBulkArchive = async () => {
    setBulkArchiving(true);
    setError(null);
    try {
      const res = await phappsRequest(API_PATHS.DIALPAD_CALLS_ARCHIVE_BULK, "POST", { ids: selectionModel });
      setBulkConfirm(false);
      setSelectionModel([]);
      setBulkResult(res);
      fetchCalls();
    } catch {
      setError("Failed to archive the selected calls. Please try again.");
    } finally {
      setBulkArchiving(false);
    }
  };

  const handleArchiveConfirm = async () => {
    if (!archiving) return;
    setArchivingId(archiving.id);
    setArchiving(null);
    try {
      await phappsRequest(API_PATHS.DIALPAD_CALLS_ARCHIVE(archiving.id), "POST", {});
      fetchCalls();
    } catch {
      setError("Failed to archive call. Please try again.");
    } finally {
      setArchivingId(null);
    }
  };

  const columns = [
    {
      field: "start_time", headerName: "Date / Time", width: 170,
      renderCell: p => <Typography variant="body2" component="span">{fmtDateTime(p.value)}</Typography>,
    },
    {
      field: "direction", headerName: "Dir", width: 60, align: "center", headerAlign: "center",
      renderCell: p => p.value === "inbound"
        ? <CallReceivedIcon fontSize="small" sx={{ color: "#059669" }} />
        : <CallMadeIcon fontSize="small" sx={{ color: "#2563eb" }} />,
    },
    { field: "external_number", headerName: "Number", width: 150 },
    {
      field: "duration_seconds", headerName: "Duration", width: 90,
      renderCell: p => <Typography variant="body2" component="span">{fmtDuration(p.value)}</Typography>,
    },
    { field: "manager_name", headerName: "Manager", width: 130 },
    {
      field: "ai_summary", headerName: "Summary", flex: 1, minWidth: 200, sortable: false,
      renderCell: p => {
        const raw = p.value;
        if (!raw) {
          return (
            <Typography variant="body2" component="span" color="text.disabled" sx={{ fontStyle: "italic" }}>
              {p.row.has_ai_summary ? "Loading…" : "No summary"}
            </Typography>
          );
        }
        // ai_summary may be a JSON string — extract the text portion
        let text = raw;
        if (typeof raw === "string") {
          try {
            const parsed = JSON.parse(raw);
            text = parsed.summary ?? parsed.body ?? parsed.content ?? parsed.text ?? raw;
          } catch {
            text = raw;
          }
        }
        const truncated = typeof text === "string" && text.length > 120
          ? text.slice(0, 120).trimEnd() + "…"
          : text;
        return (
          <Tooltip title={typeof text === "string" ? text : ""} placement="top-start" arrow>
            <Typography variant="body2" component="span" sx={{ lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
              {truncated}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "transcript_btn", headerName: "", width: 48, sortable: false, align: "center", headerAlign: "center",
      renderCell: p => (
        <Tooltip title={p.row.has_transcript || p.row.has_ai_summary ? "View summary & transcript" : "No transcript available"}>
          <span>
            <IconButton
              size="small"
              disabled={!p.row.has_transcript && !p.row.has_ai_summary}
              onClick={() => setViewingCall(p.row)}
              sx={{ color: p.row.has_transcript || p.row.has_ai_summary ? DIALPAD_PINK : undefined }}
            >
              <ArticleOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      field: "actions", headerName: "", width: 175, sortable: false,
      renderCell: p => (
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          <Button
            size="small" variant="outlined"
            onClick={() => setAllocating(p.row)}
            sx={{ textTransform: "none", borderColor: DIALPAD_PINK, color: DIALPAD_PINK, fontWeight: 600 }}
          >
            Allocate
          </Button>
          <Tooltip title="Ignore — archive this call (voicemail, empty call, etc.)">
            <span>
              <IconButton
                size="small"
                disabled={archivingId === p.row.id}
                onClick={() => setArchiving(p.row)}
                sx={{ color: "#9ca3af", "&:hover": { color: "#6b7280", backgroundColor: "#f3f4f6" } }}
              >
                {archivingId === p.row.id
                  ? <CircularProgress size={14} sx={{ color: "#9ca3af" }} />
                  : <ArchiveOutlinedIcon fontSize="small" />
                }
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box", p: 2, gap: 1.25, overflow: "hidden" }}>

      <AdminBackLink />

      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: DIALPAD_PINK, lineHeight: 1 }}>
              Unallocated Calls
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Calls not yet linked to a chemist or job card.
            </Typography>
          </Box>
        </Box>

        {/* Bulk archive — shown when one or more rows are selected */}
        {selectionModel.length > 0 && (
          <Button
            size="small"
            variant="contained"
            startIcon={<ArchiveOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setBulkConfirm(true)}
            sx={{
              textTransform: "none", fontWeight: 700, fontSize: "0.78rem",
              backgroundColor: "#6b7280", boxShadow: "none",
              "&:hover": { backgroundColor: "#4b5563" },
            }}
          >
            Archive selected ({selectionModel.length})
          </Button>
        )}

        {/* Re-scan button */}
        <Tooltip title="Re-scan all unallocated calls against current chemist phone numbers">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={scanning
                ? <CircularProgress size={13} sx={{ color: DIALPAD_PINK }} />
                : <RefreshIcon sx={{ fontSize: 16 }} />
              }
              disabled={scanning}
              onClick={handleRescan}
              sx={{
                textTransform: "none", fontWeight: 600, fontSize: "0.78rem",
                borderColor: DIALPAD_PINK, color: DIALPAD_PINK,
                "&:hover": { backgroundColor: "#FDE8F3", borderColor: DIALPAD_PINK },
              }}
            >
              {scanning ? "Scanning…" : "Re-scan"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Scan result banner */}
      {scanResult && (
        <Alert
          severity={scanResult.matched > 0 ? "success" : "info"}
          onClose={() => setScanResult(null)}
          sx={{ flexShrink: 0, py: 0.5 }}
        >
          {scanResult.matched > 0
            ? `Re-scan complete — ${scanResult.matched} call${scanResult.matched !== 1 ? "s" : ""} automatically matched and allocated (${scanResult.total_scanned} scanned).`
            : `Re-scan complete — no new matches found across ${scanResult.total_scanned} call${scanResult.total_scanned !== 1 ? "s" : ""}.`
          }
        </Alert>
      )}

      {bulkResult && (
        <Alert severity="success" onClose={() => setBulkResult(null)} sx={{ flexShrink: 0, py: 0.5 }}>
          {`Archived ${bulkResult.archived} call${bulkResult.archived !== 1 ? "s" : ""}.`}
          {bulkResult.skipped > 0 ? ` ${bulkResult.skipped} skipped (you can only archive your own calls).` : ""}
        </Alert>
      )}

      {error && <Alert severity="warning" sx={{ flexShrink: 0 }}>{error}</Alert>}

      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          paginationModel={{ page, pageSize: 50 }}
          onPaginationModelChange={m => { setPage(m.page); setSelectionModel([]); }}
          pageSizeOptions={[50]}
          checkboxSelection
          keepNonExistentRowsSelected
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          disableColumnMenu
          disableRowSelectionOnClick
          density="compact"
          sx={{
            height: "100%",
            ...pharmacyHaloDataGridSx,
            "& .MuiDataGrid-columnHeader": { backgroundColor: "#F58A2E", color: "#fff" },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#FFFBEB !important" },
            "& .MuiDataGrid-cell": { alignItems: "center" },
          }}
        />
      </Box>

      <AllocateCallModal
        open={!!allocating}
        onClose={() => setAllocating(null)}
        call={allocating}
        onAllocated={fetchCalls}
      />

      <CallDetailModal
        open={!!viewingCall}
        onClose={() => setViewingCall(null)}
        call={viewingCall}
      />

      {/* Archive confirmation dialog */}
      <Dialog
        open={!!archiving}
        onClose={() => setArchiving(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }
        }}
      >
        {/* Coloured top bar */}
        <Box sx={{ height: 4, backgroundColor: "#6b7280", flexShrink: 0 }} />

        <DialogContent sx={{ pt: 3, pb: 1.5, px: 3 }}>
          {/* Icon + title row */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 2, flexShrink: 0,
              backgroundColor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ArchiveOutlinedIcon sx={{ fontSize: 22, color: "#6b7280" }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#111827", lineHeight: 1.3 }}>
                Archive this call?
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, lineHeight: 1.5 }}>
                It will be hidden from the unallocated list. Use this for voicemails, empty calls, or anything that doesn't need to be allocated.
              </Typography>
            </Box>
          </Box>

          {/* Call detail pill */}
          {archiving && (
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              px: 1.5, py: 1.25, borderRadius: 2,
              backgroundColor: "#f9fafb", border: "1px solid #e5e7eb",
            }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                backgroundColor: archiving.direction === "inbound" ? "#d1fae5" : "#dbeafe",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {archiving.direction === "inbound"
                  ? <CallReceivedIcon sx={{ fontSize: 15, color: "#059669" }} />
                  : <CallMadeIcon sx={{ fontSize: 15, color: "#2563eb" }} />
                }
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#111827" }}>
                  {archiving.external_number || "Unknown number"}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {archiving.manager_name || "—"}{archiving.start_time ? ` · ${fmtDateTime(archiving.start_time)}` : ""}
                  {archiving.duration_seconds ? ` · ${fmtDuration(archiving.duration_seconds)}` : ""}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            fullWidth
            onClick={() => setArchiving(null)}
            sx={{
              textTransform: "none", fontWeight: 600, borderRadius: 2,
              border: "1px solid #e5e7eb", color: "#374151",
              "&:hover": { backgroundColor: "#f9fafb" },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleArchiveConfirm}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2,
              backgroundColor: "#6b7280",
              "&:hover": { backgroundColor: "#4b5563" },
              boxShadow: "none",
            }}
          >
            Archive call
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk archive confirmation dialog */}
      <Dialog
        open={bulkConfirm}
        onClose={() => !bulkArchiving && setBulkConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" } }}
      >
        <Box sx={{ height: 4, backgroundColor: "#6b7280", flexShrink: 0 }} />
        <DialogContent sx={{ pt: 3, pb: 1.5, px: 3 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 2, flexShrink: 0,
              backgroundColor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ArchiveOutlinedIcon sx={{ fontSize: 22, color: "#6b7280" }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#111827", lineHeight: 1.3 }}>
                Archive {selectionModel.length} selected call{selectionModel.length !== 1 ? "s" : ""}?
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, lineHeight: 1.5 }}>
                They'll be hidden from the unallocated list.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            fullWidth
            disabled={bulkArchiving}
            onClick={() => setBulkConfirm(false)}
            sx={{
              textTransform: "none", fontWeight: 600, borderRadius: 2,
              border: "1px solid #e5e7eb", color: "#374151",
              "&:hover": { backgroundColor: "#f9fafb" },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={bulkArchiving}
            startIcon={bulkArchiving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            onClick={handleBulkArchive}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2,
              backgroundColor: "#6b7280",
              "&:hover": { backgroundColor: "#4b5563" },
              boxShadow: "none",
            }}
          >
            {bulkArchiving ? "Archiving…" : `Archive ${selectionModel.length}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDialpadUnallocatedPage;
